import { SelectablePaymentOption } from '~/features/checkout/components/Payment/types';
import { useCheckoutState } from '~/features/checkout/hooks/useCheckoutState';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { BetterStackLogLevel, clientLog } from '~/lib/better-stack';
import { resetAltaPaySession } from '~/services/altapay/hooks';
import { isMissingBasketError } from '~/services/altapay/lib/errors';
import { ClientSideSingleton as AltaPaySession } from '~/services/altapay/session';
import { parseError } from '~/shared/utils/parse-error';

export const usePaymentInitiationError = () => {
    const { orderNumber, setOrderNumber } = useCheckoutState();
    const { basketId } = useBasket();

    const handlePaymentInitiationError = (
        error: unknown,
        paymentOption: SelectablePaymentOption,
    ) => {
        // Read before the reset below clears them.
        const context = {
            orderNumber,
            basketId,
            sessionId: AltaPaySession.getSessionId(),
            sessionAgeInSeconds: AltaPaySession.getSessionAgeInSeconds(),
        };

        if (isMissingBasketError(error)) {
            /**
             * The basket was most likely turned into an order by /verify in an earlier payment attempt.
             * Its order number and session belong to that order, so we cannot use them again.
             */
            setOrderNumber(undefined);
            resetAltaPaySession();

            clientLog({
                event: 'Checkout:BasketNoLongerExists',
                message: `Basket ${basketId} no longer exists. Payment with ${paymentOption.name} was stopped.`,
                data: {
                    error: parseError(error),
                    paymentOption: paymentOption.name,
                    ...context,
                },
            });

            return;
        }

        clientLog({
            event: 'Checkout:PaymentInitiationError',
            level: BetterStackLogLevel.NOTICE,
            message: `Payment with ${paymentOption.name} cannot be initiated.`,
            data: {
                error: parseError(error),
                paymentOption,
                ...context,
            },
        });
    };

    return { handlePaymentInitiationError };
};
